import { useCallback, useSyncExternalStore } from "react";

import { captureException } from "@sentry/react";
import toast from "react-hot-toast";

import {
  useSetupCharterMutation,
  WorkGroupDocument,
} from "@/generated/graphql";
import { withNavigationSource } from "@/lib/navigation-source";
import { useRootNavigate } from "@/routes";

// Both call sites can be on screen at once, the rail card and a feed action,
// and each `useMutation` tracks its own `loading`. Disabling the CTA that was
// clicked therefore left the other one live, and a second click started a
// concurrent insert. pantheon's `setup_student_charter/3` is a read followed
// by an insert with no transaction, and `Charter.changeset/2` declares no
// unique constraint, so the loser of that race is an uncaught
// `Ecto.ConstraintError` rather than a changeset error. Keyed by group id
// because that is the mutation's scope: two students in different groups are
// not racing each other.
const inFlightGroups = new Set<string>();
const inFlightListeners = new Set<() => void>();

function setGroupInFlight(groupId: string, active: boolean) {
  if (active) {
    inFlightGroups.add(groupId);
  } else {
    inFlightGroups.delete(groupId);
  }
  inFlightListeners.forEach((notify) => notify());
}

function subscribeToInFlight(notify: () => void) {
  inFlightListeners.add(notify);
  return () => {
    inFlightListeners.delete(notify);
  };
}

/**
 * Creates a group's charter and navigates to it. `GroupCharterCard`'s CTA and
 * the feed's `charter` action both run this sequence, so it lives here once
 * rather than as two call sites that could drift apart.
 */
export function useSetupCharter(
  groupId: string | undefined,
  sourceMarker?: { source: string; sourceType: string }
) {
  const rootNavigate = useRootNavigate();
  const [setupCharterMutation, { loading }] = useSetupCharterMutation();
  const groupInFlight = useSyncExternalStore(subscribeToInFlight, () =>
    groupId == null ? false : inFlightGroups.has(groupId)
  );

  const setupCharter = useCallback(() => {
    // Callers hold the group id in state that starts undefined, so guard here
    // rather than let a click mid-load send an empty id to the mutation.
    if (groupId == null) return;

    if (inFlightGroups.has(groupId)) return;

    setGroupInFlight(groupId, true);
    return setupCharterMutation({
      variables: { groupId },
      // Deliberately without `awaitRefetchQueries`. Holding the shared lock
      // until this refetch lands reads as the safer option, but it makes a
      // failed refetch reject a mutation that succeeded: `onCompleted` never
      // runs, so the student stays on Home with an error toast for a charter
      // that exists, and the next click resyncs it to a newer form version.
      // The stale-cache window that would close is already covered, by
      // navigating away below and by `workGroupPending` holding both create
      // CTAs on the way back.
      refetchQueries: [WorkGroupDocument],
      onCompleted: (data) => {
        if (data?.setupCharter.id) {
          const charterUrl = `/charter/${data.setupCharter.id}`;
          rootNavigate(
            sourceMarker
              ? withNavigationSource(
                  charterUrl,
                  sourceMarker.source,
                  sourceMarker.sourceType
                )
              : charterUrl
          );
        }
      },
      // Both callers fire this from a click handler with no `await`. Without
      // `onError` a failure was an unhandled rejection, and nothing caught it:
      // instrument.ts's explicit `integrations` list omits
      // `globalHandlersIntegration`, so Sentry never sees one. Supplying
      // `onError` also makes the mutate promise resolve rather than reject.
      onError: (error) => {
        toast.error(error.message);
        captureException(error);
      },
    }).finally(() => setGroupInFlight(groupId, false));
  }, [setupCharterMutation, groupId, rootNavigate, sourceMarker]);

  return { setupCharter, loading: loading || groupInFlight };
}
